(function () {
    'use strict';

    angular
        .module('elogbooksServices')
        .service('loginService', loginService);

    loginService.$inject = [
        '$rootScope',
        '$state',
        '$window',
        '$q',
        'apiClient',
        'vcRecaptchaService',
        'userManager'
    ];

    function loginService($rootScope, $state, $window, $q, apiClient, vcRecaptchaService, userManager) {
        var service = this;

        service.RECAPTCHA_KEY = '6LfhEEIUAAAAAFvkgLosgt7xogSYfelgjvECRgAZ';

        // Browsers can't be relied on to prefill the username on step 1: a form
        // with no password field isn't a login form to them, so filling it depends
        // on Chromium's crowdsourced single-username predictions for this origin,
        // which we neither control nor get on every installation. Remember it
        // ourselves instead.
        //
        // A username isn't a credential, but persisting it across sessions still
        // discloses who last used the machine, so it's opt-in and never inferred.
        var REMEMBER_USERNAME_ENABLED_KEY = 'rememberUsernameEnabled';
        var REMEMBERED_USERNAME_KEY = 'rememberedUsername';

        service.fetchNextHint = fetchNextHint;
        service.isRememberUsernameEnabled = isRememberUsernameEnabled;
        service.setRememberUsernameEnabled = setRememberUsernameEnabled;
        service.rememberUsername = rememberUsername;
        service.getRememberedUsername = getRememberedUsername;
        service.initVm = initVm;
        service.recaptchaSetResponse = recaptchaSetResponse;
        service.recaptchaSetWidgetId = recaptchaSetWidgetId;
        service.recaptchaSetExpiration = recaptchaSetExpiration;
        service.checkEmail = checkEmail;
        service.returnToLogin = returnToLogin;
        service.toggleLink = toggleLink;
        service.cancelForgotUsername = cancelForgotUsername;
        service.redirectIfLoggedIn = redirectIfLoggedIn;

        // Resolves to 'sso', 'password', or null when the hint can't be determined.
        function fetchNextHint(username) {
            if (!username) {
                return $q.when(null);
            }

            return apiClient.get('/sso/next-hint', { username: username }, false).then(function (response) {
                return response && typeof response.nextHint === 'string' ? response.nextHint : null;
            }, function () {
                return null;
            });
        }

        function isRememberUsernameEnabled() {
            return $window.localStorage.getItem(REMEMBER_USERNAME_ENABLED_KEY) === 'true';
        }

        // Opting out has to forget immediately, not at the next successful login.
        function setRememberUsernameEnabled(enabled) {
            if (enabled) {
                $window.localStorage.setItem(REMEMBER_USERNAME_ENABLED_KEY, 'true');
                return;
            }

            $window.localStorage.removeItem(REMEMBER_USERNAME_ENABLED_KEY);
            $window.localStorage.removeItem(REMEMBERED_USERNAME_KEY);
        }

        // Only call once the username is known to be real - a completed login, or
        // an 'sso' hint, which the backend only returns for an actual SSO user.
        // Storing on entry would remember typos.
        function rememberUsername(username) {
            if (!username || !isRememberUsernameEnabled()) {
                return;
            }

            $window.localStorage.setItem(REMEMBERED_USERNAME_KEY, username);
        }

        function getRememberedUsername() {
            return isRememberUsernameEnabled()
                ? $window.localStorage.getItem(REMEMBERED_USERNAME_KEY)
                : null;
        }

        function initVm(vm, rootResource, $stateParams) {
            vm.helpdeskPhoneNumber = rootResource ? rootResource.helpdeskPhoneNumber : null;
            vm.trainingUrl = rootResource && rootResource.trainingUrl
                ? rootResource.trainingUrl
                : 'https://e-logbooks.astute-elearning.com/';
            vm.loginFailMsg = $rootScope.loginFailMsg || null;
            vm.recaptchaCheck = $window.localStorage.getItem('recaptchaRequired') ? true : false;
            vm.recaptchaResponse = null;
            vm.recaptchaKey = service.RECAPTCHA_KEY;
            vm.loading = false;
            vm.usernameError = false;
            vm.returnButton = $rootScope.returnButton || false;
            vm.userTrainingRequired = $rootScope.userTrainingRequired || false;
            vm.themePrefix = rootResource?.theme ?? 'cafm';
            vm.themeLogo = '/' + vm.themePrefix + '-logo.png';
            vm.themeFormerlyLogo = '/' + vm.themePrefix + '-formerly-logo.png';
            vm.email = null;
            vm.rememberUsername = isRememberUsernameEnabled();
            // Trimmed: '?username=%20%20' would otherwise prefill and submit whitespace.
            var username = ($stateParams && $stateParams.username)
                || $rootScope.username
                || getRememberedUsername();

            vm.credentials = {
                username: username ? username.trim() : username,
                password: null,
                source: 'web'
            };
        }

        function recaptchaSetResponse(vm, response) {
            vm.recaptchaResponse = response;
        }

        function recaptchaSetWidgetId(vm, widgetId) {
            vm.widgetId = widgetId;
        }

        function recaptchaSetExpiration(vm) {
            vcRecaptchaService.reload(vm.widgetId);
        }

        function checkEmail(form) {
            return form.email.$invalid && form.$submitted;
        }

        function returnToLogin(vm, form) {
            vm.loginFailMsg = $rootScope.loginFailMsg = null;
            vm.usernameError = null;
            vm.returnButton = $rootScope.returnButton = false;
            vm.userTrainingRequired = $rootScope.userTrainingRequired = false;
            vm.credentials.password = null;
            vm.credentials.username = null;
            form.$setPristine();
            $state.go('login.step1');
        }

        function toggleLink(vm, type) {
            vm.loginFailMsg = null;
            $rootScope.loginFailMsg = null;

            switch (type) {
                case 'forgottenUsername':
                    vm.forgotUsername = !vm.forgotUsername;
                    break;
                case 'forgottenPassword':
                    vm.forgotPassword = !vm.forgotPassword;
                    break;
            }
        }

        function cancelForgotUsername(vm, form) {
            vm.forgotUsername = !vm.forgotUsername;
            vm.email = vm.loginFailMsg = $rootScope.loginFailMsg = vm.usernameError = null;
            form.$setPristine();
        }

        // Returns whether it sent the user away.
        function redirectIfLoggedIn() {
            if (userManager.getToken()) {
                if (userManager.hasPermission("site_permission_helpdesk") &&
                    !userManager.hasPermission("user_permission_manage_users")) {
                    $state.go('dashboard.helpdesk.dashboard.quotes.chase').then(function() {
                        $rootScope.username = null;
                    });
                } else {
                    $state.go('dashboard.user.dashboard').then(function() {
                        $rootScope.username = null;
                    });
                }

                return true;
            }

            return false;
        }
    }
})();
